import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { parseNotes, renderNotes, ensureStyle, isFirstStyling, splitSections, joinSections } from './notesDoc';
import { useDialog, useToast } from './ui/dialogs';
import { IconPlus, IconTrash, IconArrowUp, IconArrowDown } from './icons';

/*
 * HubNotesEditor — the page is a list of NOTES, each a heading and its content.
 *
 *   Notes     one card per note: edit its heading and its body on their own,
 *             add a note, delete one, move it up or down.
 *   Markdown  the same page as one document, for pasting or bulk edits.
 *   Preview   what the reader sees, live.
 *
 * Both tabs edit the SAME markdown — notes are just `## heading` sections of
 * it, split and re-joined losslessly. That keeps one source of truth, so the
 * style map (which callout is a warning, which flow step is mint…) still keys
 * off stable blocks: the first save generates the look, later saves only carry
 * the words. "Restyle" is the explicit way to ask for a fresh pass.
 */
const STARTER_NOTE = {
  heading: 'New note',
  body: 'What it is, in a short paragraph.\n\n> [!TIP] Key idea: the thing worth remembering.',
};

const HELP = [
  ['**bold**  *italic*  `code`', 'inline emphasis'],
  ['> [!TIP] text', 'tip callout · [!WARN] warning · [!SAY] what to say'],
  ['=> text', 'the one-line definition'],
  ['### Sub-label', 'a small blue caps label inside the note'],
  ['```formula', 'a centred formula block'],
  ['```flow', 'A -> B -> C  (use :: for a second line)'],
  ['```tokens', 'label, then pieces separated by |'],
  ['? question', 'a Q&A item; the line under it is the answer'],
  ['| a | b |', 'table · ![alt](src) image · - and 1. lists'],
];

/* One-click formatting for a note body — writes the same markdown the page
   renders, so learners never have to remember the syntax. */
const FORMATS = [
  { id: 'bold', label: 'B', title: 'Bold', wrap: ['**', '**'] },
  { id: 'italic', label: 'I', title: 'Italic', wrap: ['*', '*'] },
  { id: 'list', label: '• List', title: 'Bulleted list', prefix: '- ' },
  { id: 'olist', label: '1. List', title: 'Numbered list', prefix: '1. ' },
  { id: 'takeaway', label: 'Key takeaway', title: 'A highlighted key takeaway', prefix: '> [!TIP] ' },
  { id: 'example', label: 'Example', title: 'A small "Example" label', prefix: '### Example\n' },
];
const applyFormat = (el, fmt, onChange) => {
  if (!el) return;
  const { value, selectionStart: a, selectionEnd: b } = el;
  let next; let caret;
  if (fmt.wrap) {
    const sel = value.slice(a, b) || fmt.title.toLowerCase();
    next = `${value.slice(0, a)}${fmt.wrap[0]}${sel}${fmt.wrap[1]}${value.slice(b)}`;
    caret = a + fmt.wrap[0].length + sel.length + fmt.wrap[1].length;
  } else {
    const start = value.lastIndexOf('\n', a - 1) + 1;      // start of the current line
    const block = value.slice(start, b) || '';
    const lines = (block || '').split('\n').map((l) => `${fmt.prefix}${l}`);
    next = `${value.slice(0, start)}${lines.join('\n')}${value.slice(Math.max(b, start))}`;
    caret = start + lines.join('\n').length;
  }
  onChange(next);
  requestAnimationFrame(() => { el.focus(); el.setSelectionRange(caret, caret); });
};

const HubNotesEditor = ({
  title: initialTitle = '', markdown: initialMarkdown = '', style: initialStyle = null,
  forkedFrom = null, busy = false, onSave, onCancel, onDelete,
}) => {
  const initial = useMemo(() => splitSections(initialMarkdown), [initialMarkdown]);
  const [title, setTitle] = useState(initialTitle || initial.title);
  const [lead, setLead] = useState(initial.lead);
  const [notes, setNotes] = useState(() => (initial.sections.length ? initial.sections : [STARTER_NOTE]));
  const [tab, setTab] = useState('notes');           // notes | markdown | preview
  const bodyRefs = useRef({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const styleRef = useRef(initialStyle);
  const { confirm } = useDialog();
  const toast = useToast();
  const firstTime = isFirstStyling(styleRef.current);

  const markdown = useMemo(() => joinSections({ title, lead, sections: notes }), [title, lead, notes]);
  const dirty = markdown !== (initialMarkdown || '') || title !== initialTitle;

  // The preview uses the SAVED style, topped up for notes that are new in this
  // edit — exactly what a save would write.
  const { doc, style } = useMemo(() => {
    const parsed = parseNotes(markdown);
    return { doc: parsed, style: ensureStyle(parsed, styleRef.current) };
  }, [markdown]);

  const patchNote = (i, patch) => setNotes((ns) => ns.map((n, k) => (k === i ? { ...n, ...patch } : n)));
  const addNote = (at) => setNotes((ns) => {
    const next = ns.slice();
    next.splice(at ?? ns.length, 0, { ...STARTER_NOTE });
    return next;
  });
  const moveNote = (i, by) => setNotes((ns) => {
    const j = i + by;
    if (j < 0 || j >= ns.length) return ns;
    const next = ns.slice();
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const removeNote = useCallback(async (i) => {
    const n = notes[i];
    if (n.heading.trim() || n.body.trim()) {
      const ok = await confirm({
        title: `Delete the note "${n.heading || 'Untitled note'}"?`,
        message: 'Its text is removed from this page. Nothing else on the page changes.',
        confirmLabel: 'Delete note', danger: true,
      });
      if (!ok) return;
    }
    setNotes((ns) => ns.filter((_, k) => k !== i));
  }, [notes, confirm]);

  const save = useCallback(async () => {
    const name = (title || doc.title || 'Untitled page').trim();
    setSaving(true);
    setErr('');
    try {
      const nextStyle = ensureStyle(doc, styleRef.current);
      await onSave({
        name,
        markdown,
        style: nextStyle,
        intro: lead || (doc.concepts[0]?.blocks.find((b) => b.t === 'p')?.text || ''),
      });
      styleRef.current = nextStyle;
      toast.success(firstTime ? 'Page saved — styling generated' : 'Page saved');
    } catch (e) {
      setErr(e.message || 'Could not save this page.');
    } finally {
      setSaving(false);
    }
  }, [title, lead, markdown, doc, onSave, toast, firstTime]);

  const restyle = useCallback(async () => {
    const ok = await confirm({
      title: 'Restyle this page?',
      message: 'Colours and highlights are chosen again from the current text. The words are untouched.',
      confirmLabel: 'Restyle',
    });
    if (!ok) return;
    styleRef.current = null;
    setNotes((ns) => ns.slice());        // re-run the preview against a blank style
    toast.info('Styling regenerated — save to keep it');
  }, [confirm, toast]);

  const cancel = useCallback(async () => {
    if (dirty) {
      const ok = await confirm({
        title: 'Discard your changes?',
        message: 'This page goes back to the last saved version.',
        confirmLabel: 'Discard', danger: true,
      });
      if (!ok) return;
    }
    onCancel();
  }, [dirty, confirm, onCancel]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) { e.preventDefault(); save(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save]);

  return (
    <div className="hb-editor">
      <div className="hb-ed-bar">
        <span className="ttl">
          <input value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder={doc.title || 'Page title'} aria-label="Page title" />
        </span>
        <span className={`hb-ed-note${firstTime ? ' on' : ''}`}>
          {firstTime ? 'Styling on first save' : `${notes.length} note${notes.length === 1 ? '' : 's'} · styling kept`}
        </span>
        {err && <span className="hb-ed-err">{err}</span>}
        <div className="hb-ed-tabs" role="tablist" aria-label="Editor view">
          {[['notes', 'Notes'], ['markdown', 'Markdown'], ['preview', 'Preview']].map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id}
              className={tab === id ? 'on' : ''} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>
        {!firstTime && <button className="hb-btn" type="button" onClick={restyle}>Restyle</button>}
        {onDelete && <button className="hb-btn" type="button" onClick={onDelete}>Delete page</button>}
        <button className="hb-btn" type="button" onClick={cancel}>Cancel</button>
        <button className="hb-btn go" type="button" onClick={save} disabled={saving || busy}>
          {saving ? 'Saving…' : 'Save page'}
        </button>
      </div>

      {forkedFrom && (
        <div className="hb-ed-lbl">Your own copy — the shared page it came from is untouched.</div>
      )}

      <div className={`hb-ed-body${tab === 'notes' ? ' notes-only' : ''}`}>
        {tab === 'notes' && (
          <div className="hb-ed-pane">
            <div className="hb-ed-lbl">
              Notes
              <span className="sp">{notes.length} on this page</span>
            </div>
            <div className="hb-ed-notes">
              <label className="hb-ed-lead">
                <span>Lead — the line under the title</span>
                <textarea value={lead} rows={2} placeholder="One sentence that says what this page is about."
                  onChange={(e) => setLead(e.target.value)} />
              </label>

              {notes.map((n, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <article className="hb-note" key={`note-${i}`}>
                  <div className="hb-note-head">
                    <span className="hb-note-num">{String(i + 1).padStart(2, '0')}</span>
                    <input className="hb-note-title" value={n.heading} placeholder="Note heading"
                      aria-label={`Note ${i + 1} heading`}
                      onChange={(e) => patchNote(i, { heading: e.target.value })} />
                    <span className="hb-note-acts">
                      <button type="button" title="Move up" aria-label={`Move note ${i + 1} up`}
                        disabled={i === 0} onClick={() => moveNote(i, -1)}><IconArrowUp size={13} /></button>
                      <button type="button" title="Move down" aria-label={`Move note ${i + 1} down`}
                        disabled={i === notes.length - 1} onClick={() => moveNote(i, 1)}><IconArrowDown size={13} /></button>
                      <button type="button" title="Delete this note" aria-label={`Delete note ${i + 1}`}
                        onClick={() => removeNote(i)}><IconTrash size={13} /></button>
                    </span>
                  </div>
                  <div className="kh-fmt" role="toolbar" aria-label={`Format note ${i + 1}`}>
                    {FORMATS.map((f) => (
                      <button key={f.id} type="button" title={f.title}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => applyFormat(bodyRefs.current[i], f, (body) => patchNote(i, { body }))}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <textarea className="hb-note-body" value={n.body} rows={7}
                    ref={(el) => { bodyRefs.current[i] = el; }}
                    aria-label={`Note ${i + 1} content`}
                    placeholder="What it is, in a short paragraph. Use **bold**, lists, tables, > [!TIP] callouts…"
                    onChange={(e) => patchNote(i, { body: e.target.value })} />
                  <button type="button" className="hb-note-insert" onClick={() => addNote(i + 1)}>
                    <IconPlus size={12} /> Add a note here
                  </button>
                </article>
              ))}

              <button type="button" className="hb-btn go hb-note-add" onClick={() => addNote()}>
                <IconPlus size={13} /> Add note
              </button>
            </div>
          </div>
        )}

        {tab === 'markdown' && (
          <div className="hb-ed-pane">
            <div className="hb-ed-lbl">
              Markdown
              <span className="sp">{markdown.length.toLocaleString()} chars</span>
            </div>
            <textarea
              className="hb-ed-ta"
              value={markdown}
              onChange={(e) => {
                const next = splitSections(e.target.value);
                setTitle(next.title || title);
                setLead(next.lead);
                setNotes(next.sections.length ? next.sections : [{ heading: '', body: '' }]);
              }}
              spellCheck="true"
              aria-label="Page markdown"
              placeholder="Write the page in Markdown…"
            />
          </div>
        )}

        <div className="hb-ed-pane">
          <div className="hb-ed-lbl">Page</div>
          <div className="hb-ed-prev" role="region" aria-label="Page preview">
            <div className="hb-page">
              <div className="hb-hero">
                <div className="hb-hero-tag">Preview</div>
                <h1>{title || doc.title || 'Untitled page'}</h1>
                {doc.lead && <p>{doc.lead}</p>}
              </div>
              {doc.concepts.length ? renderNotes(doc, style)
                : <div className="hb-empty">Add a note to start this page.</div>}
            </div>
          </div>
        </div>
      </div>

      <details className="hb-ed-help">
        <summary>Markdown that becomes design</summary>
        <table><tbody>
          {HELP.map(([syntax, what]) => (
            <tr key={syntax}><td>{syntax}</td><td>{what}</td></tr>
          ))}
        </tbody></table>
      </details>
    </div>
  );
};

export default HubNotesEditor;
